import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import UserMenu from "./UserMenu";
import SearchModal from "./SearchModal";
import { FiSun, FiMoon } from "react-icons/fi";

const logos = {
	icon: "/images/logos/blackadder-logo-icon.png",
	fullWhite: "/images/logos/blackadder-ticket-logo-typo-w.svg",
};

interface HeaderProps {
	simpleMode?: boolean; // シンプルモード（ロゴとメニューボタンのみ）
}

const Header: React.FC<HeaderProps> = ({ simpleMode = false }) => {
	const { isAuthenticated, logout } = useAuth();
	const { theme, toggleTheme } = useTheme();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
	const [expandedSections, setExpandedSections] = useState<string[]>([]);
	const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

	useEffect(() => {
		// Prevent body scroll when menu is open
		if (isMenuOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isMenuOpen]);

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
		setIsUserMenuOpen(false);
		if (!isMenuOpen) {
			setExpandedSections([]); // Reset expanded sections when opening menu
		}
	};

	const toggleUserMenu = () => {
		setIsUserMenuOpen(!isUserMenuOpen);
	};

	const closeMenus = () => {
		setIsMenuOpen(false);
		setIsUserMenuOpen(false);
	};

	const toggleSection = (section: string) => {
		setExpandedSections((prev) =>
			prev.includes(section)
				? prev.filter((s) => s !== section)
				: [...prev, section]
		);
	};

	return (
		<header
			className={`h-20 ${theme === "light" ? "text-gray-900" : "text-white"}`}
		>
			<div className="container mx-auto px-6 h-full">
				<div className="flex justify-between items-center h-full">
					<Link to="/" className="flex items-center space-x-3">
						<img
							className={`h-10 shadow-md ${theme === "dark" ? "invert" : ""}`}
							src={logos.icon}
							alt="Blackadder Icon"
							width="auto"
						/>
						<img
							className={`h-4 ${theme === "light" ? "invert" : ""}`}
							src={logos.fullWhite}
							alt="Blackadder"
							width="auto"
						/>
					</Link>

					{/* Desktop Navigation - Hide in simple mode */}
					{!simpleMode && (
						<nav className="hidden md:flex items-center space-x-2">
							<div className="relative group">
								<button
									className={`px-3 sm:px-4 py-2 rounded-lg transition-colors duration-200 flex items-center ${
										theme === "light"
											? "hover:bg-gray-200"
											: "hover:bg-gray-700"
									}`}
								>
									イベントを探す
									<svg
										className={`w-4 h-4 ml-1 ${
											theme === "light" ? "text-gray-600" : "text-gray-400"
										}`}
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M19 9l-7 7-7-7"
										></path>
									</svg>
								</button>
								{/* Invisible bridge to prevent hover gap */}
								<div className="absolute left-0 top-full w-56 h-2 group-hover:block"></div>
								<div
									className={`absolute left-0 top-full mt-2 w-56 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 ${
										theme === "light" ? "bg-white" : "bg-gray-800"
									}`}
								>
									<div className="py-2">
										<button
											onClick={() => setIsSearchModalOpen(true)}
											className={`w-full text-left block px-4 py-3 transition-colors duration-200 ${
												theme === "light"
													? "hover:bg-gray-100"
													: "hover:bg-gray-700"
											}`}
										>
											<div className="flex items-center justify-between">
												<span>検索</span>
												<svg
													className={`w-4 h-4 ${
														theme === "light"
															? "text-gray-600"
															: "text-gray-400"
													}`}
													fill="none"
													stroke="currentColor"
													strokeWidth="1.5"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
													/>
												</svg>
											</div>
										</button>
										<Link
											to="/events"
											className={`block px-4 py-3 transition-colors duration-200 ${
												theme === "light"
													? "hover:bg-gray-100"
													: "hover:bg-gray-700"
											}`}
										>
											<div className="flex items-center justify-between">
												<span>イベント一覧</span>
												<svg
													className={`w-4 h-4 ${
														theme === "light"
															? "text-gray-600"
															: "text-gray-400"
													}`}
													fill="none"
													stroke="currentColor"
													strokeWidth="1.5"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
													/>
												</svg>
											</div>
										</Link>
									</div>
								</div>
							</div>

							<div className="relative group">
								<button
									className={`px-3 sm:px-4 py-2 rounded-lg transition-colors duration-200 flex items-center ${
										theme === "light"
											? "hover:bg-gray-200"
											: "hover:bg-gray-700"
									}`}
								>
									主催者向け
									<svg
										className={`w-4 h-4 ml-1 ${
											theme === "light" ? "text-gray-600" : "text-gray-400"
										}`}
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											d="M19 9l-7 7-7-7"
										></path>
									</svg>
								</button>
								{/* Invisible bridge to prevent hover gap */}
								<div className="absolute left-0 top-full w-56 h-2 group-hover:block"></div>
								<div
									className={`absolute left-0 mt-2 w-56 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 ${
										theme === "light" ? "bg-white" : "bg-gray-800"
									}`}
								>
									<div className="py-2">
										<Link
											to="/organizer"
											className={`block px-4 py-3 transition-colors duration-200 ${
												theme === "light"
													? "hover:bg-gray-100"
													: "hover:bg-gray-700"
											}`}
										>
											<div className="flex items-center justify-between">
												<span>ダッシュボード</span>
												<svg
													className={`w-4 h-4 ${
														theme === "light"
															? "text-gray-600"
															: "text-gray-400"
													}`}
													fill="none"
													stroke="currentColor"
													strokeWidth="1.5"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
													/>
												</svg>
											</div>
										</Link>
										<Link
											to="/organizer/events/create"
											className={`block px-4 py-3 transition-colors duration-200 ${
												theme === "light"
													? "hover:bg-gray-100"
													: "hover:bg-gray-700"
											}`}
										>
											<div className="flex items-center justify-between">
												<span>イベント作成</span>
												<svg
													className={`w-4 h-4 ${
														theme === "light"
															? "text-gray-600"
															: "text-gray-400"
													}`}
													fill="none"
													stroke="currentColor"
													strokeWidth="1.5"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M12 4.5v15m7.5-7.5h-15"
													/>
												</svg>
											</div>
										</Link>
										<Link
											to="/organizer/start"
											className={`block px-4 py-3 transition-colors duration-200 ${
												theme === "light"
													? "hover:bg-gray-100"
													: "hover:bg-gray-700"
											}`}
										>
											<div className="flex items-center justify-between">
												<span>主催者登録</span>
												<svg
													className={`w-4 h-4 ${
														theme === "light"
															? "text-gray-600"
															: "text-gray-400"
													}`}
													fill="none"
													stroke="currentColor"
													strokeWidth="1.5"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
													/>
												</svg>
											</div>
										</Link>
									</div>
								</div>
							</div>

							<Link
								to="/help"
								className={`px-3 sm:px-4 py-2 rounded-lg transition-colors duration-200 ${
									theme === "light" ? "hover:bg-gray-200" : "hover:bg-gray-700"
								}`}
							>
								ヘルプ
							</Link>

							{/* Dark Mode Toggle Button */}
							<button
								onClick={toggleTheme}
								className={`h-10 w-10 rounded-lg transition-colors duration-200 flex items-center justify-center ${
									theme === "light" ? "hover:bg-gray-200" : "hover:bg-gray-700"
								}`}
								aria-label={
									theme === "light"
										? "ダークモードに切り替え"
										: "ライトモードに切り替え"
								}
							>
								{theme === "light" ? (
									<FiMoon className="w-5 h-5" />
								) : (
									<FiSun className="w-5 h-5" />
								)}
							</button>

							{isAuthenticated ? (
								<div className="relative">
									<button
										onClick={toggleUserMenu}
										className={`px-3 sm:px-4 py-2 rounded-lg transition-colors duration-200 flex items-center ${
											theme === "light"
												? "bg-gray-200 hover:bg-gray-300"
												: "bg-gray-700 hover:bg-gray-600"
										}`}
									>
										マイページ
										<svg
											className={`w-4 h-4 ml-1 ${
												theme === "light" ? "text-gray-600" : "text-gray-400"
											}`}
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M19 9l-7 7-7-7"
											></path>
										</svg>
									</button>
									{isUserMenuOpen && (
										<div
											className={`absolute right-0 mt-2 w-56 rounded-lg shadow-xl z-50 ${
												theme === "light" ? "bg-white" : "bg-gray-800"
											}`}
										>
											<div className="py-2">
												<Link
													to="/mypage"
													className={`block px-4 py-3 transition-colors duration-200 ${
														theme === "light"
															? "hover:bg-gray-100"
															: "hover:bg-gray-700"
													}`}
													onClick={closeMenus}
												>
													<div className="flex items-center justify-between">
														<span>マイページ</span>
														<svg
															className={`w-4 h-4 ${
																theme === "light"
																	? "text-gray-600"
																	: "text-gray-400"
															}`}
															fill="none"
															stroke="currentColor"
															strokeWidth="1.5"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
															/>
														</svg>
													</div>
												</Link>
												<Link
													to="/mypage/history"
													className={`block px-4 py-3 transition-colors duration-200 ${
														theme === "light"
															? "hover:bg-gray-100"
															: "hover:bg-gray-700"
													}`}
													onClick={closeMenus}
												>
													<div className="flex items-center justify-between">
														<span>購入履歴</span>
														<svg
															className={`w-4 h-4 ${
																theme === "light"
																	? "text-gray-600"
																	: "text-gray-400"
															}`}
															fill="none"
															stroke="currentColor"
															strokeWidth="1.5"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
															/>
														</svg>
													</div>
												</Link>
												<Link
													to="/selling-history"
													className={`block px-4 py-3 transition-colors duration-200 ${
														theme === "light"
															? "hover:bg-gray-100"
															: "hover:bg-gray-700"
													}`}
													onClick={closeMenus}
												>
													<div className="flex items-center justify-between">
														<span>出品履歴</span>
														<svg
															className={`w-4 h-4 ${
																theme === "light"
																	? "text-gray-600"
																	: "text-gray-400"
															}`}
															fill="none"
															stroke="currentColor"
															strokeWidth="1.5"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
															/>
														</svg>
													</div>
												</Link>
												<hr
													className={`my-2 ${
														theme === "light"
															? "border-gray-300"
															: "border-gray-600"
													}`}
												/>
												<button
													onClick={logout}
													className={`block w-full text-left px-4 py-3 transition-colors duration-200 ${
														theme === "light"
															? "hover:bg-gray-100"
															: "hover:bg-gray-700"
													}`}
												>
													<div className="flex items-center justify-between">
														<span>ログアウト</span>
														<svg
															className={`w-4 h-4 ${
																theme === "light"
																	? "text-gray-600"
																	: "text-gray-400"
															}`}
															fill="none"
															stroke="currentColor"
															strokeWidth="1.5"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
															/>
														</svg>
													</div>
												</button>
											</div>
										</div>
									)}
								</div>
							) : (
								<UserMenu />
							)}
						</nav>
					)}

					{/* Mobile Menu Button */}
					<div className="md:hidden">
						<button onClick={toggleMenu} aria-label="メニュー">
							<svg
								className="w-6 h-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M4 6h16M4 12h16M4 18h16"
								></path>
							</svg>
						</button>
					</div>
				</div>
			</div>

			{/* Full-screen mobile menu */}
			<div
				className={`fixed inset-0 transform transition-transform duration-300 ease-in-out z-50 md:hidden ${
					isMenuOpen ? "translate-x-0" : "-translate-x-full"
				} ${
					theme === "light"
						? "bg-white text-gray-900"
						: "bg-gray-900 text-white"
				}`}
			>
				<div className="p-6 h-full flex flex-col overflow-y-auto">
					{/* Header with logo and close button */}
					<div className="flex justify-between items-center mb-12">
						<div className="text-2xl font-semibold">blackadder LLC</div>
						<button
							onClick={toggleMenu}
							className={`transition-colors ${
								theme === "light"
									? "text-gray-900 hover:text-gray-600"
									: "text-white hover:text-gray-400"
							}`}
							aria-label="メニューを閉じる"
						>
							<svg
								className="w-8 h-8"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>

					{/* Menu items */}
					<nav className="flex-1">
						<div className="space-y-6 text-lg">
							{/* イベントチケットを探す Section */}
							<div>
								<button
									onClick={() => toggleSection("events")}
									className={`w-full flex items-center justify-between group transition-colors ${
										theme === "light"
											? "hover:text-gray-600"
											: "hover:text-gray-300"
									}`}
								>
									<span>イベントを探す</span>
									{expandedSections.includes("events") ? (
										<svg
											className={`w-5 h-5 group-hover:text-gray-300 ${
												theme === "light" ? "text-gray-600" : "text-gray-400"
											}`}
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M19.5 8.25l-7.5 7.5-7.5-7.5"
											/>
										</svg>
									) : (
										<svg
											className={`w-5 h-5 group-hover:text-gray-300 ${
												theme === "light" ? "text-gray-600" : "text-gray-400"
											}`}
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M8.25 4.5l7.5 7.5-7.5 7.5"
											/>
										</svg>
									)}
								</button>
								{expandedSections.includes("events") && (
									<div className="mt-4 ml-6 space-y-4">
										<button
											onClick={() => {
												setIsSearchModalOpen(true);
												toggleMenu();
											}}
											className={`w-full text-left flex items-center justify-between group transition-colors ${
												theme === "light"
													? "hover:text-gray-600"
													: "hover:text-gray-300"
											}`}
										>
											<span className="text-base">検索</span>
											<svg
												className={`w-4 h-4 group-hover:text-gray-300 ${
													theme === "light" ? "text-gray-600" : "text-gray-400"
												}`}
												fill="none"
												stroke="currentColor"
												strokeWidth="1.5"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
												/>
											</svg>
										</button>
										<Link
											to="/events"
											onClick={toggleMenu}
											className={`flex items-center justify-between group transition-colors ${
												theme === "light"
													? "hover:text-gray-600"
													: "hover:text-gray-300"
											}`}
										>
											<span className="text-base">イベント一覧</span>
											<svg
												className={`w-4 h-4 group-hover:text-gray-300 ${
													theme === "light" ? "text-gray-600" : "text-gray-400"
												}`}
												fill="none"
												stroke="currentColor"
												strokeWidth="1.5"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
												/>
											</svg>
										</Link>
									</div>
								)}
							</div>

							{/* 主催者向け Section */}
							<div>
								<button
									onClick={() => toggleSection("organizer")}
									className={`w-full flex items-center justify-between group transition-colors ${
										theme === "light"
											? "hover:text-gray-600"
											: "hover:text-gray-300"
									}`}
								>
									<span>主催者向け</span>
									{expandedSections.includes("organizer") ? (
										<svg
											className={`w-5 h-5 group-hover:text-gray-300 ${
												theme === "light" ? "text-gray-600" : "text-gray-400"
											}`}
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M19.5 8.25l-7.5 7.5-7.5-7.5"
											/>
										</svg>
									) : (
										<svg
											className={`w-5 h-5 group-hover:text-gray-300 ${
												theme === "light" ? "text-gray-600" : "text-gray-400"
											}`}
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M8.25 4.5l7.5 7.5-7.5 7.5"
											/>
										</svg>
									)}
								</button>
								{expandedSections.includes("organizer") && (
									<div className="mt-4 ml-6 space-y-4">
										<Link
											to="/organizer"
											onClick={toggleMenu}
											className={`flex items-center justify-between group transition-colors ${
												theme === "light"
													? "hover:text-gray-600"
													: "hover:text-gray-300"
											}`}
										>
											<span className="text-base">ダッシュボード</span>
											<svg
												className={`w-4 h-4 group-hover:text-gray-300 ${
													theme === "light" ? "text-gray-600" : "text-gray-400"
												}`}
												fill="none"
												stroke="currentColor"
												strokeWidth="1.5"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
												/>
											</svg>
										</Link>
										<Link
											to="/organizer/events/create"
											onClick={toggleMenu}
											className={`flex items-center justify-between group transition-colors ${
												theme === "light"
													? "hover:text-gray-600"
													: "hover:text-gray-300"
											}`}
										>
											<span className="text-base">イベント作成</span>
											<svg
												className={`w-4 h-4 group-hover:text-gray-300 ${
													theme === "light" ? "text-gray-600" : "text-gray-400"
												}`}
												fill="none"
												stroke="currentColor"
												strokeWidth="1.5"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
												/>
											</svg>
										</Link>
										<Link
											to="/organizer/start"
											onClick={toggleMenu}
											className={`flex items-center justify-between group transition-colors ${
												theme === "light"
													? "hover:text-gray-600"
													: "hover:text-gray-300"
											}`}
										>
											<span className="text-base">主催者登録</span>
											<svg
												className={`w-4 h-4 group-hover:text-gray-300 ${
													theme === "light" ? "text-gray-600" : "text-gray-400"
												}`}
												fill="none"
												stroke="currentColor"
												strokeWidth="1.5"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
												/>
											</svg>
										</Link>
									</div>
								)}
							</div>

							{/* マイページ Section (for authenticated users) */}
							{isAuthenticated && (
								<div>
									<button
										onClick={() => toggleSection("mypage")}
										className={`w-full flex items-center justify-between group transition-colors ${
											theme === "light"
												? "hover:text-gray-600"
												: "hover:text-gray-300"
										}`}
									>
										<span>マイページ</span>
										{expandedSections.includes("mypage") ? (
											<svg
												className={`w-5 h-5 group-hover:text-gray-300 ${
													theme === "light" ? "text-gray-600" : "text-gray-400"
												}`}
												fill="none"
												stroke="currentColor"
												strokeWidth="1.5"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M19.5 8.25l-7.5 7.5-7.5-7.5"
												/>
											</svg>
										) : (
											<svg
												className="w-5 h-5 text-gray-400 group-hover:text-gray-300"
												fill="none"
												stroke="currentColor"
												strokeWidth="1.5"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M8.25 4.5l7.5 7.5-7.5 7.5"
												/>
											</svg>
										)}
									</button>
									{expandedSections.includes("mypage") && (
										<div className="mt-4 ml-6 space-y-4">
											<Link
												to="/mypage"
												onClick={toggleMenu}
												className={`flex items-center justify-between group transition-colors ${
													theme === "light"
														? "hover:text-gray-600"
														: "hover:text-gray-300"
												}`}
											>
												<span className="text-base">マイページトップ</span>
												<svg
													className={`w-4 h-4 group-hover:text-gray-300 ${
														theme === "light"
															? "text-gray-600"
															: "text-gray-400"
													}`}
													fill="none"
													stroke="currentColor"
													strokeWidth="1.5"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
													/>
												</svg>
											</Link>
											<Link
												to="/mypage/history"
												onClick={toggleMenu}
												className={`flex items-center justify-between group transition-colors ${
													theme === "light"
														? "hover:text-gray-600"
														: "hover:text-gray-300"
												}`}
											>
												<span className="text-base">購入履歴</span>
												<svg
													className={`w-4 h-4 group-hover:text-gray-300 ${
														theme === "light"
															? "text-gray-600"
															: "text-gray-400"
													}`}
													fill="none"
													stroke="currentColor"
													strokeWidth="1.5"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
													/>
												</svg>
											</Link>
											<Link
												to="/selling-history"
												onClick={toggleMenu}
												className={`flex items-center justify-between group transition-colors ${
													theme === "light"
														? "hover:text-gray-600"
														: "hover:text-gray-300"
												}`}
											>
												<span className="text-base">出品履歴</span>
												<svg
													className={`w-4 h-4 group-hover:text-gray-300 ${
														theme === "light"
															? "text-gray-600"
															: "text-gray-400"
													}`}
													fill="none"
													stroke="currentColor"
													strokeWidth="1.5"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25"
													/>
												</svg>
											</Link>
										</div>
									)}
								</div>
							)}

							<Link
								to="/help"
								onClick={toggleMenu}
								className={`flex items-center justify-between group transition-colors ${
									theme === "light"
										? "hover:text-gray-600"
										: "hover:text-gray-300"
								}`}
							>
								<span>ヘルプ/サポート</span>
								<svg
									className={`w-5 h-5 group-hover:text-gray-300 ${
										theme === "light" ? "text-gray-600" : "text-gray-400"
									}`}
									fill="none"
									stroke="currentColor"
									strokeWidth="1.5"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M8.25 4.5l7.5 7.5-7.5 7.5"
									/>
								</svg>
							</Link>

							{/* Auth Links */}
							{isAuthenticated ? (
								<button
									onClick={() => {
										logout();
										toggleMenu();
									}}
									className={`w-full flex items-center justify-between group transition-colors ${
										theme === "light"
											? "hover:text-gray-600"
											: "hover:text-gray-300"
									}`}
								>
									<span>ログアウト</span>
									<svg
										className={`w-5 h-5 group-hover:text-gray-300 ${
											theme === "light" ? "text-gray-600" : "text-gray-400"
										}`}
										fill="none"
										stroke="currentColor"
										strokeWidth="1.5"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M8.25 4.5l7.5 7.5-7.5 7.5"
										/>
									</svg>
								</button>
							) : (
								<>
									<Link
										to="/login"
										onClick={() => {
											localStorage.setItem(
												"previous_url",
												window.location.pathname
											);
											toggleMenu();
										}}
										className={`flex items-center justify-between group transition-colors ${
											theme === "light"
												? "hover:text-gray-600"
												: "hover:text-gray-300"
										}`}
									>
										<span>ログイン</span>
										<svg
											className={`w-5 h-5 group-hover:text-gray-300 ${
												theme === "light" ? "text-gray-600" : "text-gray-400"
											}`}
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M8.25 4.5l7.5 7.5-7.5 7.5"
											/>
										</svg>
									</Link>
									<Link
										to="/register"
										onClick={() => {
											localStorage.setItem(
												"previous_url",
												window.location.pathname
											);
											toggleMenu();
										}}
										className={`flex items-center justify-between group transition-colors ${
											theme === "light"
												? "hover:text-gray-600"
												: "hover:text-gray-300"
										}`}
									>
										<span>新規登録</span>
										<svg
											className={`w-5 h-5 group-hover:text-gray-300 ${
												theme === "light" ? "text-gray-600" : "text-gray-400"
											}`}
											fill="none"
											stroke="currentColor"
											strokeWidth="1.5"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M8.25 4.5l7.5 7.5-7.5 7.5"
											/>
										</svg>
									</Link>
								</>
							)}
						</div>
					</nav>

					{/* Dark Mode Toggle - Bottom Left */}
					<div className="mt-auto pt-6">
						<button
							onClick={toggleTheme}
							className={`p-3 rounded-lg transition-colors ${
								theme === "light"
									? "bg-gray-200 hover:bg-gray-300"
									: "bg-gray-800 hover:bg-gray-700"
							}`}
							aria-label={
								theme === "light"
									? "ダークモードに切り替え"
									: "ライトモードに切り替え"
							}
						>
							{theme === "light" ? (
								<FiMoon className="w-6 h-6 text-gray-700" />
							) : (
								<FiSun className="w-6 h-6 text-gray-300" />
							)}
						</button>
					</div>
				</div>
			</div>

			{/* Search Modal */}
			<SearchModal
				isOpen={isSearchModalOpen}
				onClose={() => setIsSearchModalOpen(false)}
			/>
		</header>
	);
};

export default Header;
